<template>
  <component :is="embedded ? 'div' : Page">
    <div class="max-w-5xl" data-cy="zapier-subscriptions-page">
      <h1 v-if="!embedded" class="page-header-1 mb-2">Zapier subscriptions</h1>
      <p class="mb-6 max-w-3xl text-sm text-gray-600">
        Each Zap that uses a Sera trigger subscribes here, and every event Sera
        sends to it is a delivery. Sera can't see your Zap names, so
        subscriptions are listed by trigger and Zapier account. To turn a Zap
        off or change it, manage your Zaps in
        <a
          href="https://zapier.com/app/zaps"
          target="_blank"
          rel="noopener"
          class="link"
          >Zapier</a
        >.
      </p>

      <div v-if="loading" class="flex justify-center py-10">
        <Spinner />
      </div>

      <Alert v-else-if="loadFailed" type="error" data-cy="zapier-load-error">
        Your Zapier subscriptions could not be loaded.
        <button type="button" class="link ml-1" @click="load">Try again</button>
      </Alert>

      <template v-else>
        <section class="mb-8" data-cy="zapier-active-section">
          <h2 class="mb-3 text-lg font-semibold text-gray-900">
            Active
            <span class="text-sm font-normal text-gray-500"
              >({{ active.length }})</span
            >
          </h2>

          <p
            v-if="!active.length"
            class="rounded-lg border border-gray-200 bg-white p-4 text-sm text-gray-600"
            data-cy="zapier-active-empty"
          >
            No Zaps are subscribed to Sera triggers. Turn on a Zap that uses a
            Sera trigger and it will appear here.
          </p>

          <div
            v-for="group in groups"
            :key="group.key"
            class="mb-4 overflow-hidden rounded-lg border border-gray-200 bg-white"
            :data-cy="`zapier-group:${group.key}`"
          >
            <div
              class="flex items-center justify-between border-b border-gray-200 bg-gray-50 px-4 py-2"
            >
              <h3
                class="m-0 text-sm font-semibold text-gray-900"
                data-cy="zapier-group-label"
              >
                {{ group.label }}
              </h3>
              <span class="text-xs text-gray-500">
                {{ group.subscriptions.length }}
                {{
                  group.subscriptions.length === 1
                    ? "subscription"
                    : "subscriptions"
                }}
              </span>
            </div>
            <button
              v-for="sub in group.subscriptions"
              :key="sub.id"
              type="button"
              class="grid w-full grid-cols-1 gap-1 border-b border-gray-100 px-4 py-3 text-left text-sm last:border-b-0 hover:bg-gray-50 md:grid-cols-[2fr_2fr_1.5fr] md:gap-4"
              :data-cy="`zapier-subscription:${sub.id}`"
              @click="selected = sub"
            >
              <div>
                <div class="font-medium text-gray-900">
                  {{ sub.trigger.label }}
                  <span
                    v-if="isEngine(sub)"
                    class="ml-1 whitespace-nowrap rounded-full bg-blue-50 px-2 py-0.5 text-xs font-semibold text-blue-700"
                    :title="ENGINE_BADGE_TITLE"
                    data-cy="zapier-engine-badge"
                    >{{ ENGINE_BADGE }}</span
                  >
                </div>
                <div class="text-xs text-gray-500">
                  <code :title="FINGERPRINT_TITLE">{{
                    sub.destination.fingerprint
                  }}</code>
                  · Added {{ formatDateWithTimezone(sub.created_at) }}
                </div>
              </div>
              <div data-cy="zapier-last-delivery">
                <template v-if="sub.last_delivery">
                  <span
                    class="mr-2 whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-semibold"
                    :class="statusStyle(sub.last_delivery.status).pill"
                    >{{ statusStyle(sub.last_delivery.status).label }}</span
                  >
                  <span
                    class="text-gray-700"
                    :title="formatDateTimeWithTimezone(sub.last_delivery.at)"
                    >{{ fromNow(sub.last_delivery.at, true) }}</span
                  >
                </template>
                <span v-else class="text-gray-500"
                  >No deliveries in 30 days</span
                >
              </div>
              <div class="text-gray-700" data-cy="zapier-counts">
                <span class="text-green-700"
                  >{{ formatCount(sub.counts_30d.success) }} succeeded</span
                >
                ·
                <span
                  :class="
                    sub.counts_30d.failure ? 'text-red-700' : 'text-gray-500'
                  "
                  >{{ formatCount(sub.counts_30d.failure) }} failed</span
                >
                <span class="block text-xs text-gray-500">last 30 days</span>
              </div>
            </button>
          </div>
        </section>

        <section v-if="stopped.length" data-cy="zapier-stopped-section">
          <h2 class="mb-1 text-lg font-semibold text-gray-900">
            Stopped in the last 30 days
          </h2>
          <p class="mb-3 text-sm text-gray-600">{{ RESUME_HINT }}</p>
          <div
            class="overflow-hidden rounded-lg border border-gray-200 bg-white"
          >
            <button
              v-for="sub in stopped"
              :key="sub.id"
              type="button"
              class="grid w-full grid-cols-1 gap-1 border-b border-gray-100 px-4 py-3 text-left text-sm last:border-b-0 hover:bg-gray-50 md:grid-cols-[2fr_3fr] md:gap-4"
              :data-cy="`zapier-stopped:${sub.id}`"
              @click="selected = sub"
            >
              <div>
                <div class="font-medium text-gray-900">
                  {{ sub.trigger.label }}
                  <span
                    v-if="isEngine(sub)"
                    class="ml-1 whitespace-nowrap rounded-full bg-blue-50 px-2 py-0.5 text-xs font-semibold text-blue-700"
                    :title="ENGINE_BADGE_TITLE"
                    data-cy="zapier-engine-badge"
                    >{{ ENGINE_BADGE }}</span
                  >
                </div>
                <div class="text-xs text-gray-500">
                  {{ destinationLabel(sub) }} ·
                  <code :title="FINGERPRINT_TITLE">{{
                    sub.destination.fingerprint
                  }}</code>
                </div>
              </div>
              <div>
                <div
                  class="font-medium text-gray-900"
                  data-cy="zapier-stopped-reason"
                >
                  {{ stoppedReasonCopy(sub.archived_reason) }}
                </div>
                <div class="text-xs text-gray-500">
                  {{ RESUME_HINT }}
                </div>
              </div>
            </button>
          </div>
        </section>
      </template>

      <ZapierDeliveriesDrawer
        v-if="selected"
        :subscription="selected"
        @close="selected = null"
      />
    </div>
  </component>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useStore } from "vuex";
import {
  formatDateTimeWithTimezone,
  formatDateWithTimezone
} from "@/lib/helpers";

import Page from "@/components/Page.vue";
import Alert from "@/components/Seranova/Alert/Alert.vue";
import Spinner from "@/components/Seranova/Spinner/Spinner.vue";
import { fromNow } from "@/mixins";
import type {
  ZapierSubscription,
  ZapierSubscriptionsResponse
} from "@/vuex/modules/zapierSubscriptions";
import ZapierDeliveriesDrawer from "./ZapierDeliveriesDrawer.vue";
import {
  destinationLabel,
  ENGINE_BADGE,
  ENGINE_BADGE_TITLE,
  groupActiveSubscriptions,
  isEngine,
  RESUME_HINT,
  statusStyle,
  stoppedReasonCopy
} from "./zapierPanel";

// Inside the Zapier hub's Subscriptions tab: no page wrapper or heading of its
// own, and the hub sets the breadcrumbs.
const props = defineProps<{ embedded?: boolean }>();

const FINGERPRINT_TITLE =
  "Tells apart subscriptions on the same account. Sera never shows the hook URL.";

const store = useStore();

const subscriptions = ref<ZapierSubscription[]>([]);
const loading = ref(false);
const loadFailed = ref(false);
const selected = ref<ZapierSubscription | null>(null);

const active = computed(() =>
  subscriptions.value.filter((sub) => sub.state === "active")
);
const stopped = computed(() =>
  subscriptions.value.filter((sub) => sub.state === "stopped")
);
const groups = computed(() => groupActiveSubscriptions(active.value));

const formatCount = (n: number) => (n ?? 0).toLocaleString("en-US");

async function load() {
  loading.value = true;
  loadFailed.value = false;
  try {
    const response: ZapierSubscriptionsResponse = await store.dispatch(
      "fetchZapierSubscriptions"
    );
    subscriptions.value = response.data;
  } catch {
    loadFailed.value = true;
    window.notify.error("Could not load Zapier subscriptions.");
  } finally {
    loading.value = false;
  }
}

// Drive the app-header breadcrumb (Marketplace › Zapier) explicitly, as
// CustomFormBuilder and the import pages do. The nav's DOM-derived fallback
// can't: this route isn't a sidebar link, so it would show only the page title.
// Set after the load so it lands after the router's deferred regeneration.
onMounted(async () => {
  await load();
  if (props.embedded) return;
  store.dispatch("setBreadcrumbs", [
    { label: "Marketplace", url: "/marketplace" },
    { label: "Zapier" }
  ]);
});
</script>
